import { IconButton, Typography, colors } from '@sweep-io/sweep-design';
import usePopover from '../../hooks/usePopover';
import { MoreActionsHorizontal } from '@sweep-io/sweep-design/dist/icons/MoreActionsHorizontal';
import { MenuItem, Popover, Stack, Tooltip } from '@mui/material';
import ConfirmDestructiveDialog from '../common/dialogs/ConfirmDestructiveDialog';
import { useState } from 'react';
import { useAccountSelectionApiFacade } from '../../apis/facades/useAccountSelectionApiFacade';
import { telemetry } from '../../telemetry';
import { useSweepNotifications } from '../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../reducers/notificationsReducer';
import { useAccountSelectionContext } from './AccountSelectionContext';
import { useSelector } from 'react-redux';
import { selectUserInfoData } from '../../reducers/userInfoReducer';

interface AccountActionMenuProps {
  accountId: string;
  isDeletable: boolean;
  updateChosenAccountsState: (deletedAccountId: string) => void;
}

export const AccountActionMenu = ({
  accountId,
  isDeletable,
  updateChosenAccountsState,
}: AccountActionMenuProps) => {
  const userInfo = useSelector(selectUserInfoData);
  const isCurrentlyLoadedAccount = userInfo?.account?.id === accountId;
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const { isPopoverOpen, closePopover, openPopover, anchorElement, anchorRef } = usePopover();
  const { delete_account } = useAccountSelectionApiFacade();
  const { addNotification } = useSweepNotifications();
  const { removeAccountFromState } = useAccountSelectionContext();

  const disconnectAccount = async (accountId: string) => {
    try {
      removeAccountFromState(accountId);
      updateChosenAccountsState(accountId);
      await delete_account(accountId);
      addNotification({
        message: 'Account deleted successfully',
        variant: SweepNotificationVariant.Success,
      });
    } catch (error) {
      telemetry.captureError('Failed to delete account', error);
      addNotification({
        message: 'Failed to delete account',
        variant: SweepNotificationVariant.Error,
      });
    } finally {
      setIsConfirmDialogOpen(false);
    }
  };

  //block currently loaded account from being disabled
  if (isCurrentlyLoadedAccount) {
    return null;
  }

  return (
    <Stack onClick={(e) => e.stopPropagation()}>
      <ConfirmDestructiveDialog
        open={isConfirmDialogOpen}
        onConfirm={async () => await disconnectAccount(accountId)}
        onClose={() => setIsConfirmDialogOpen(false)}
        confirmText="Delete"
        title="Delete account?"
        children={
          <Typography variant="body" color={colors.grey[700]}>
            This action will permanently remove this account from Sweep.
          </Typography>
        }
      />

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          openPopover(e);
        }}
        ref={anchorRef}
        size="small"
        variant="flat"
        pressed={isPopoverOpen}
      >
        <MoreActionsHorizontal />
      </IconButton>

      <Popover
        open={isPopoverOpen}
        anchorEl={anchorElement}
        disablePortal
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack p={1}>
          <Tooltip
            disableHoverListener={isDeletable}
            title="Only accounts where you're the only user can be disabled."
          >
            <span>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  closePopover();
                  setIsConfirmDialogOpen(true);
                }}
                sx={{ width: '172px' }}
                disabled={!isDeletable}
              >
                <Typography variant="body" color={colors.blush[600]}>
                  Delete
                </Typography>
              </MenuItem>
            </span>
          </Tooltip>
        </Stack>
      </Popover>
    </Stack>
  );
};
